'use client';

import type { PropsWithChildren } from 'react';
import {
    createContext,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import type { Nullable, DeliveryType } from '@libs/core';
import { getLocale, Locale } from '@libs/core';

import type {
    HandleAddToCart,
    Product,
    ProductDetailsContextProps,
    ProductDetailsProviderProps,
} from '@app/pdp/types';
import { getProductPersonalizations } from '@app/pdp/utils';
import { getDefaultSelectedInventory } from '@app/pdp/components/AddToCart/DeliveryMethods/utils';
import {
    getAvailableColors,
    getSelectedProduct,
    getSelectedVariant,
    sortVariants,
} from '@app/pdp/utils/productVariants';
import type { MappedProductVariant, SelectedVariants } from '@libs/algolia';
import {
    syncSfccSessionCookiesWithRetry,
    useOptimizely,
} from '@libs/common-next';
import type { Store } from '@libs/common-node';

export const ProductDetailsContext =
    createContext<Nullable<ProductDetailsContextProps>>(undefined);

export const getDefaultDeliveryMethod = (
    product: Product,
    store: Nullable<Store>,
    selectedProduct: MappedProductVariant
): DeliveryType => {
    const storeDeliveryMethods = {
        // if there is no favorite store, it should allow bopis method so the user can select a store
        bopisDisabled: store && (!store.isBopisEnabled || store.bopisDisabled),
    };

    const productDeliveyMethods = {
        inStoreOnly: product['shoppingOptions-isInStoreOnly'],
        inStoreOnlyOveride: product['shoppingOptions-isInStoreOnlyOverride'],
        bopisEligible: product['shoppingOptions-isBopisEligible'],
        onlineOnly: product['shoppingOptions-isSoldOnlineOnly'],
    };

    const bopisDisabled =
        storeDeliveryMethods.bopisDisabled ||
        !productDeliveyMethods.bopisEligible ||
        Boolean(productDeliveyMethods.onlineOnly);

    const sddDisabled =
        Boolean(productDeliveyMethods.onlineOnly) ||
        !product.customScheduledDeliveryEligible;

    const inStoreOnly =
        typeof productDeliveyMethods.inStoreOnlyOveride !== 'undefined'
            ? productDeliveyMethods.inStoreOnlyOveride
            : Boolean(productDeliveyMethods.inStoreOnly);

    const isShipToHomeSupported =
        getLocale() === Locale['en-CA']
            ? Boolean(product['shipToHome-isCASupported'])
            : !inStoreOnly;

    const isAutoshipAvailable = !!selectedProduct.isSubscriptionEnabled;

    const isAutoshipSupported =
        (isShipToHomeSupported && isAutoshipAvailable) ||
        (isAutoshipAvailable && !inStoreOnly);

    return getDefaultSelectedInventory(
        bopisDisabled,
        sddDisabled,
        isAutoshipSupported,
        isShipToHomeSupported
    );
};

export default function ProductDetailsProvider({
    children,
    product,
    handleAddToCart,
    saveSthDeliveryDetails,
    wishList,
    defaultStore,
}: PropsWithChildren<ProductDetailsProviderProps>) {
    const [sddStoreId, setSddStoreId] = useState<number | undefined>();
    const [
        userHasAddedToRecurringShipmentSuccessfully,
        setUserHasAddedToRecurringShipmentSuccessfully,
    ] = useState<boolean>(false);
    const {
        productVariants: { variants, uniqueVariants },
    } = product;

    const initialMappedVariant =
        variants.find(v => v.id === product.id) ?? variants[0];

    const selectedVariant = getSelectedVariant(
        initialMappedVariant,
        uniqueVariants,
        getProductPersonalizations(initialMappedVariant)
    );

    const [selectedVariants, setSelectedVariants] =
        useState<SelectedVariants>(selectedVariant);

    const [canAddToCart, setCanAddToCart] = useState(false);

    const handleSelectedValue = useCallback((value: string, type: string) => {
        setSelectedVariants(prevState => ({
            ...prevState,
            [type]: value,
        }));
    }, []);

    sortVariants(uniqueVariants);

    const selectedProduct = getSelectedProduct(
        selectedVariants,
        variants,
        product
    );

    const defaultSelectedDeliveryMethod = useMemo(
        () => getDefaultDeliveryMethod(product, defaultStore, selectedProduct),
        [product, defaultStore, selectedProduct]
    );

    const [selectedDeliveryMethod, setSelectedDeliveryMethod] =
        useState<DeliveryType>(defaultSelectedDeliveryMethod);

    const availableColors = useMemo(
        () => getAvailableColors(selectedVariants, variants),
        [selectedVariants, variants]
    );

    const availablePersonalizations = useMemo(
        () => getProductPersonalizations(selectedProduct),
        [selectedProduct]
    );

    sortVariants(availablePersonalizations);

    const handleAddToCartWrapper: HandleAddToCart = useCallback(
        async (productData, basketId) => {
            // Ensure user SFCC session cookies are synchronized by calling the /api/session route before adding to cart
            try {
                await syncSfccSessionCookiesWithRetry();
            } catch (error) {
                console.error(
                    '[handleAddToCartWrapper] Failed to synchronize SFCC session cookies:',
                    error
                );
                return null;
            }

            return await handleAddToCart(productData, basketId);
        },
        [handleAddToCart]
    );

    const [isBopisDisabled, setIsBopisDisabled] = useState<boolean>(
        !defaultStore?.isBopisEnabled
    );
    const [isSddDisabled, setIsSddDisabled] = useState(false);
    const [isInStoreOnly, setIsInStoreOnly] = useState(false);
    const [isAutoshipAvailable, setIsAutoshipAvailable] = useState(false);
    const [isShipToHomeSupported, setIsShipToHomeSupported] = useState<boolean>(
        getLocale() === Locale['en-CA']
            ? Boolean(product['shipToHome-isCASupported'])
            : !isInStoreOnly
    );
    const [isAutoshipSupported, setIsAutoshipSupported] =
        useState<boolean>(false);
    const [isAllMethodsDisabled, setIsAllMethodsDisabled] =
        useState<boolean>(false);
    // Optimizely START
    const { context } = useOptimizely();
    const hasScrolledRef = useRef(false);
    const timeoutRef = useRef<number | null>(null);
    const delay = 250;
    function getScrollDepth(): number {
        const scrollTop = window.scrollY || window.pageYOffset;
        const pageHeight = document.documentElement.scrollHeight;
        if (pageHeight <= 0) return 100;
        return Math.round((scrollTop / pageHeight) * 100);
    }

    useEffect(() => {
        const callback = () => {
            const depth = getScrollDepth();

            if (depth >= 25 && !hasScrolledRef.current) {
                hasScrolledRef.current = true;
                context?.trackEvent('scroll_depth');
            }
        };
        const handleScroll = () => {
            if (timeoutRef.current !== null) {
                window.clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = window.setTimeout(() => {
                callback();
            }, delay);
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (timeoutRef.current !== null) {
                window.clearTimeout(timeoutRef.current);
            }
        };
    }, [context]);
    // Optimizely END

    useEffect(() => {
        const storeDeliveryMethods = {
            // if there is no favorite store, it should allow bopis method so the user can select a store
            bopisDisabled:
                defaultStore &&
                (!defaultStore.isBopisEnabled || defaultStore.bopisDisabled),
        };

        const productDeliveryMethods = {
            inStoreOnly: product['shoppingOptions-isInStoreOnly'],
            inStoreOnlyOveride:
                product['shoppingOptions-isInStoreOnlyOverride'],
            bopisEligible: product['shoppingOptions-isBopisEligible'],
            onlineOnly: product['shoppingOptions-isSoldOnlineOnly'],
        };

        setIsBopisDisabled(
            storeDeliveryMethods.bopisDisabled ||
                !productDeliveryMethods.bopisEligible ||
                Boolean(productDeliveryMethods.onlineOnly)
        );
        setIsSddDisabled(
            Boolean(productDeliveryMethods.onlineOnly) ||
                !product.customScheduledDeliveryEligible
        );
        // check for edge case where override is actually set.
        setIsInStoreOnly(
            typeof productDeliveryMethods.inStoreOnlyOveride !== 'undefined'
                ? productDeliveryMethods.inStoreOnlyOveride
                : Boolean(productDeliveryMethods.inStoreOnly)
        );
    }, [defaultStore, product]);

    useEffect(() => {
        setIsAutoshipAvailable(!!selectedProduct.isSubscriptionEnabled);
    }, [selectedProduct]);

    useEffect(() => {
        setIsShipToHomeSupported(
            getLocale() === Locale['en-CA']
                ? Boolean(product['shipToHome-isCASupported'])
                : !isInStoreOnly
        );
    }, [product, isInStoreOnly]);

    useEffect(() => {
        setIsAutoshipSupported(
            (isShipToHomeSupported && isAutoshipAvailable) ||
                (isAutoshipAvailable && !isInStoreOnly)
        );
    }, [isShipToHomeSupported, isAutoshipAvailable, isInStoreOnly]);

    useEffect(() => {
        setIsAllMethodsDisabled(
            isBopisDisabled &&
                isSddDisabled &&
                !isAutoshipAvailable &&
                isInStoreOnly
        );
    }, [isBopisDisabled, isInStoreOnly, isSddDisabled, isAutoshipAvailable]);

    const values: ProductDetailsContextProps = useMemo(
        () => ({
            userHasAddedToRecurringShipmentSuccessfully,
            setUserHasAddedToRecurringShipmentSuccessfully,
            sddStoreId,
            setSddStoreId,
            selectedVariants,
            setSelectedVariants,
            handleSelectedValue,
            product,
            availableColors,
            availablePersonalizations,
            uniqueVariants,
            selectedProduct,
            setSelectedDeliveryMethod,
            selectedDeliveryMethod,
            variants,
            handleAddToCart: handleAddToCartWrapper,
            saveSthDeliveryDetails,
            wishList,
            canAddToCart,
            setCanAddToCart,
            isBopisDisabled,
            setIsBopisDisabled,
            isSddDisabled,
            setIsSddDisabled,
            isInStoreOnly,
            setIsInStoreOnly,
            isAutoshipAvailable,
            setIsAutoshipAvailable,
            isShipToHomeSupported,
            setIsShipToHomeSupported,
            isAutoshipSupported,
            setIsAutoshipSupported,
            isAllMethodsDisabled,
            setIsAllMethodsDisabled,
        }),
        [
            userHasAddedToRecurringShipmentSuccessfully,
            setUserHasAddedToRecurringShipmentSuccessfully,
            sddStoreId,
            setSddStoreId,
            selectedVariants,
            handleSelectedValue,
            product,
            availableColors,
            availablePersonalizations,
            uniqueVariants,
            selectedProduct,
            selectedDeliveryMethod,
            variants,
            handleAddToCartWrapper,
            wishList,
            canAddToCart,
            setCanAddToCart,
            saveSthDeliveryDetails,
            isBopisDisabled,
            setIsBopisDisabled,
            isSddDisabled,
            setIsSddDisabled,
            isInStoreOnly,
            setIsInStoreOnly,
            isAutoshipAvailable,
            setIsAutoshipAvailable,
            isShipToHomeSupported,
            setIsShipToHomeSupported,
            isAutoshipSupported,
            setIsAutoshipSupported,
            isAllMethodsDisabled,
            setIsAllMethodsDisabled,
        ]
    );

    return (
        <ProductDetailsContext.Provider value={values}>
            {children}
        </ProductDetailsContext.Provider>
    );
}
